import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { LogIn, User, Lock, AlertCircle } from 'lucide-react';
import { adminApi } from '../services/adminApi';

export const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await adminApi.login(formData.username, formData.password);
      
      // Store token and user info
      localStorage.setItem('admin_token', response.access_token);
      localStorage.setItem('admin_user', JSON.stringify(response.user));
      
      // Redirect to dashboard
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid username or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1E4E9E]/10 via-white to-[#00B050]/10 flex items-center justify-center p-4">
      {/* Logo and Background Elements */}
      <div className="absolute top-8 left-8">
        <img
          src="/elzalira-logo-admin.png"
          alt="Elzalira Logo"
          className="h-16 w-auto"
        />
      </div>

      <div className="absolute top-20 right-10 w-72 h-72 bg-[#00B050]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#1E4E9E]/10 rounded-full blur-3xl"></div>

      {/* Login Card */}
      <Card className="w-full max-w-md border-0 shadow-2xl relative z-10">
        <CardHeader className="text-center space-y-2">
          <div className="w-20 h-20 bg-gradient-to-br from-[#1E4E9E] to-[#00B050] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <LogIn className="w-10 h-10 text-white" />
          </div>
          <CardTitle className="text-3xl font-bold bg-gradient-to-r from-[#1E4E9E] to-[#00B050] bg-clip-text text-transparent">
            Admin Portal
          </CardTitle>
          <CardDescription className="text-base">
            Sign in to manage Elzalira website content
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2 text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700 flex items-center space-x-2">
                <User className="w-4 h-4" />
                <span>Username</span>
              </label>
              <Input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="Enter your username"
                required
                disabled={isLoading}
                className="border-slate-200 focus:border-[#1E4E9E] focus:ring-[#1E4E9E]"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700 flex items-center space-x-2">
                <Lock className="w-4 h-4" />
                <span>Password</span>
              </label>
              <Input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                required
                disabled={isLoading}
                className="border-slate-200 focus:border-[#1E4E9E] focus:ring-[#1E4E9E]"
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-[#1E4E9E] to-[#00B050] hover:from-[#00B050] hover:to-[#1E4E9E] text-white transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
              {!isLoading && <LogIn className="ml-2 w-5 h-5" />}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-500">
            <p>Authorized personnel only</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};